import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, AlertTriangle, MessageCircle, Check, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { ensurePushSubscription, pushSupported, currentPushSubscription, unsubscribePush, PushSubscribeError } from "@/lib/push";

const POLL_MS = 60_000;
const PERMISSION_KEY = "sl_notif_perm_asked";

export default function NotificationBell() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ total: 0, alerts: 0 });
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState("unknown"); // 'granted' | 'prompt' | 'denied' | 'unsupported' | 'subscribed'

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const c = await api.get("/messages/unread-count");
      setCounts(c.data);
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [user, refresh]);

  // Refresh current push subscription status
  const refreshPushState = useCallback(async () => {
    if (!pushSupported()) { setPushState("unsupported"); return; }
    const perm = Notification.permission;
    if (perm === "denied") { setPushState("denied"); return; }
    if (perm === "default") { setPushState("prompt"); return; }
    const sub = await currentPushSubscription();
    setPushState(sub ? "subscribed" : "granted");
  }, []);

  useEffect(() => { refreshPushState(); }, [refreshPushState, user]);

  // Silently re-sync the subscription when a parent (already granted perm) reloads.
  // keeps backend in sync with the device without prompting.
  useEffect(() => {
    if (!user?.id) return;
    if (!pushSupported()) return;
    if (Notification.permission !== "granted") return;
    ensurePushSubscription({ requestPermission: false })
      .then((ok) => { if (ok) setPushState("subscribed"); })
      .catch(() => { /* silent on background re-sync */ });
  }, [user]);

  // Auto-prompt for web push permission ~4 s after login. We only auto-prompt
  // for the parent role (highest signal-to-noise: weekly digests, behaviour
  // alerts, study sessions). Other roles can still enable manually from the
  // bell dropdown.
  useEffect(() => {
    if (!user || user.role !== "parent") return;
    if (!pushSupported()) return;
    if (Notification.permission !== "default") return;
    if (localStorage.getItem(PERMISSION_KEY) === "yes") return;
    const t = setTimeout(async () => {
      localStorage.setItem(PERMISSION_KEY, "yes");
      try {
        const ok = await ensurePushSubscription({ requestPermission: true });
        if (ok) {
          setPushState("subscribed");
          toast.success("Notifications turned on.");
        }
      } catch {
        // Auto-prompt failures stay silent. User can retry from the bell.
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [user]);

  const enablePush = async () => {
    try {
      const ok = await ensurePushSubscription({ requestPermission: true });
      if (ok) {
        setPushState("subscribed");
        toast.success("Push notifications enabled on this device.");
      } else {
        // ensurePushSubscription returned false (perm dialog was dismissed but
        // not denied). Reflect that in the UI without an alarming red toast.
        setPushState(Notification.permission === "granted" ? "granted" : "prompt");
      }
    } catch (err) {
      if (err instanceof PushSubscribeError) {
        if (err.code === "permission_denied" || err.code === "private_or_blocked") setPushState("denied");
        toast.error(err.message, { duration: 7000 });
        // eslint-disable-next-line no-console
        console.warn("Push subscribe error", err.code, err.cause);
      } else {
        toast.error("Could not subscribe to push notifications. Try refreshing the page.");
        // eslint-disable-next-line no-console
        console.warn("Push subscribe unexpected error", err);
      }
    }
  };

  const disablePush = async () => {
    await unsubscribePush();
    setPushState("granted");
    toast.success("Push notifications turned off on this device.");
  };

  const sendTestPush = async () => {
    try {
      const r = await api.post("/push/test");
      if (r.data?.delivered > 0) {
        toast.success(`Test sent to ${r.data.delivered} device(s). Check your system notification tray.`);
      } else {
        toast.error("Test queued but the browser did not show a notification. Check your OS focus / notification settings.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send a test push notification.");
    }
  };

  const openPanel = async () => {
    setOpen((p) => !p);
    if (!open) {
      try {
        const r = await api.get("/messages");
        setItems((r.data || []).slice(0, 10));
      } catch { /* noop */ }
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/messages/read-all");
      setCounts({ total: 0, alerts: 0 });
      setItems((p) => p.map((m) => ({ ...m, read_at: new Date().toISOString() })));
      toast.success("Marked all as read");
    } catch { /* noop */ }
  };

  if (!user?.id) return null;

  return (
    <div className="relative" data-testid="notification-bell">
      <button onClick={openPanel} aria-label="Notifications" data-testid="notification-bell-btn"
        className="relative p-2.5 rounded-xl text-[#5C635E] hover:bg-[#F4F1EA]">
        <Bell className="h-5 w-5" />
        {counts.total > 0 && (
          <span data-testid="notification-bell-count"
            className={`absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${counts.alerts > 0 ? "bg-[#E07A5F]" : "bg-[#4F7660]"} text-white`}>
            {counts.total > 99 ? "99+" : counts.total}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-12 w-[360px] max-w-[92vw] bg-white rounded-2xl shadow-xl border border-[#2D312E]/5 z-50 overflow-hidden" data-testid="notification-panel">
            <div className="px-4 py-3 border-b border-[#2D312E]/5 flex items-center justify-between">
              <strong className="text-sm">Notifications</strong>
              {counts.total > 0 && (
                <button onClick={markAllRead} data-testid="notification-mark-all-btn"
                  className="text-xs text-[#7A827C] hover:text-[#2D312E] inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>

            {/* Push-notification banner */}
            {pushState !== "unsupported" && (
              <div className="px-4 py-3 bg-[#FFF6E8] border-b border-[#E07A5F]/20 text-xs flex items-start gap-2" data-testid="notification-push-banner">
                {pushState === "subscribed" ? (
                  <>
                    <BellRing className="h-4 w-4 text-[#81B29A] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <strong className="text-[#3A6B53]">Push on.</strong>{" "}
                      You'll get system notifications even when the tab is closed.
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <button onClick={sendTestPush} data-testid="notification-push-test"
                          className="text-[#0F1830] font-semibold hover:underline">
                          Send test
                        </button>
                        <button onClick={disablePush} data-testid="notification-push-disable"
                          className="text-[#5C635E] hover:text-[#2D312E] underline">
                          Turn off
                        </button>
                      </div>
                    </div>
                  </>
                ) : pushState === "denied" ? (
                  <>
                    <BellOff className="h-4 w-4 text-[#5C635E] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-[#5C635E]">
                      Push notifications are blocked in your browser. Open your browser's site settings to allow them.
                    </div>
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 text-[#E07A5F] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 text-[#3A3F3B]">
                      Get real-time alerts when your child's educator sends a message.
                      <button onClick={enablePush} data-testid="notification-push-enable"
                        className="block mt-1.5 text-[#E07A5F] font-semibold hover:underline">
                        Turn on push notifications →
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <ul className="max-h-[420px] overflow-auto">
              {items.length === 0 && (
                <li className="px-4 py-6 text-sm text-[#7A827C] text-center">No messages yet.</li>
              )}
              {items.map((m) => {
                const unread = !m.read_at;
                const isAlert = !!m.alert;
                return (
                  <li key={m.id} className={`px-4 py-3 border-b border-[#2D312E]/5 ${unread ? "bg-[#FFF6E8]" : ""}`}>
                    <div className="flex items-start gap-2">
                      {isAlert ? <AlertTriangle className="h-4 w-4 text-[#E07A5F] mt-0.5 flex-shrink-0" /> : <MessageCircle className="h-4 w-4 text-[#81B29A] mt-0.5 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#7A827C] mb-1">{m.from_name || ", "} · {(m.created_at || "").slice(0, 10)}</div>
                        <p className={`text-sm leading-snug ${unread ? "font-medium" : ""}`}>{(m.content || "").slice(0, 200)}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="px-4 py-3 border-t border-[#2D312E]/5 text-center">
              <Link to="/messages" onClick={() => setOpen(false)} className="text-sm text-[#E07A5F] hover:underline" data-testid="notification-open-inbox">
                Open inbox →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
